import React, { forwardRef, useId } from "react";
import {
  FormLabel,
  InputBase,
  MenuItem,
  Select as MUISelect,
  SelectChangeEvent,
  SelectProps as MUISelectProps,
  FormControl,
} from "@mui/material";
import { TextInputSkeleton } from "../TextInputSkeleton/TextInputSkeleton";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

type SelectValue = string | number;

export type SelectProps<T> = {
  options: T[];
  getDisplayValue: (option: T) => string;
  getKey: (option: T) => string | number;
  getValue: (option: T) => SelectValue;
  value?: SelectValue | "";
  onChange?: (event: SelectChangeEvent<SelectValue>) => void;
  initialLoading?: boolean;
  label?: React.ReactNode;
} & Omit<MUISelectProps<SelectValue>, "value" | "onChange" | "label">;

const SelectComponent = <T,>(
  {
    value = "",
    options,
    onChange,
    getDisplayValue,
    getKey,
    label,
    getValue,
    inputProps,
    initialLoading = false,
    disabled,
    error,
    ...restProps
  }: SelectProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
): JSX.Element => {
  const id = useId();

  return (
    <FormControl fullWidth disabled={disabled} error={error}>
      {label && <FormLabel htmlFor={id}>{label}</FormLabel>}

      {initialLoading ? (
        <TextInputSkeleton />
      ) : (
        <MUISelect<SelectValue>
          ref={ref}
          id={id}
          fullWidth
          value={value}
          variant="outlined"
          onChange={onChange}
          error={error}
          input={<InputBase inputProps={inputProps} error={error} />}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
              },
            },
          }}
          {...restProps}
        >
          {options.map((option) => (
            <MenuItem key={getKey(option)} value={getValue(option)}>
              {getDisplayValue(option)}
            </MenuItem>
          ))}
        </MUISelect>
      )}
    </FormControl>
  );
};

export const Select = forwardRef(SelectComponent) as <T>(
  props: SelectProps<T> & React.RefAttributes<HTMLDivElement>
) => JSX.Element;